import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useExperiment } from '@/state/experiments/useExperiment';
import { apiErrorMessage } from '@/state/api-error';
import { DuplicateWebsiteSelect, useDuplicateTarget } from '../Experiments/DuplicateWebsiteSelect';

type DialogActionTypes = 'delete' | 'archive' | 'duplicate';
type ConfirmDialogsProps = {
  experiment: any;
  dialogAction: DialogActionTypes;
  setDialogAction: (action: DialogActionTypes | null) => void;
};

export const ConfirmDialogs = (props: ConfirmDialogsProps) => {
  const { experiment, dialogAction, setDialogAction } = props;
  const { id, name } = experiment;
  const [duplicateName, setDuplicateName] = useState(name + ' (copy)');
  const { deleteExperiment, archiveExperiment, duplicateExperiment } = useExperiment(0);
  const duplicateTarget = useDuplicateTarget({ experimentWebsiteId: experiment.websiteId });
  const isOpen = !!dialogAction;
  const { title, text } = DIALOG_DATA[dialogAction] || {};

  const onClose = () => {
    setDialogAction(null);
  };

  /**
   * Stays open on failure and says why. There was no catch here at all, so a refused
   * request (a blank name, or a website outside the account) closed nothing and said
   * nothing. apiErrorMessage rather than instanceof Error: the API client throws a plain
   * object, and a 400 or 404 reaches no global dialog.
   */
  const onConfirm = async () => {
    try {
      if (dialogAction === 'duplicate') {
        await duplicateExperiment(id, duplicateName, duplicateTarget.targetWebsiteId);
        if (duplicateTarget.isMove) toast.success(`Copied to ${duplicateTarget.targetUrl ?? 'the other website'}.`);
      } else if (dialogAction === 'archive') {
        await archiveExperiment(id);
      } else {
        await deleteExperiment(id);
      }
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, FALLBACK_MESSAGE[dialogAction]));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => setDialogAction(null)}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="grid gap-2">
          <DialogTitle className="text-title font-medium">{title}</DialogTitle>
          <DialogDescription className="supporting">{text}</DialogDescription>
        </DialogHeader>
        {/* The id is per-row, and prefixed with the screen: `htmlFor` was pointing at a
            `name` id that no element carried, and because `for` wins over the wrapping,
            the label named nothing at all. The Library's duplicate dialog labels the
            same field, so the prefix keeps the two from ever colliding. */}
        {dialogAction === 'duplicate' && (
          <Label htmlFor={`home-duplicate-name-${id}`} className="micro-label my-5 grid gap-2">
            New Experiment Name:
            <Input
              id={`home-duplicate-name-${id}`}
              className="border-line bg-field h-9 rounded-lg text-body"
              value={duplicateName}
              onChange={(ev) => setDuplicateName(ev.target.value)}
            />
          </Label>
        )}
        {dialogAction === 'duplicate' && (
          <DuplicateWebsiteSelect
            id={`home-duplicate-website-${id}`}
            value={duplicateTarget.targetWebsiteId}
            onChange={duplicateTarget.setTargetWebsiteId}
          />
        )}
        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button onClick={onConfirm} type="submit">
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Only reached when the thrown value carries no message of its own. */
const FALLBACK_MESSAGE: Record<DialogActionTypes, string> = {
  duplicate: 'Could not duplicate the experiment.',
  archive: 'Could not archive the experiment.',
  delete: 'Could not delete the experiment.',
};

const DIALOG_DATA = {
  duplicate: {
    title: 'Duplicate Experiment',
    text: 'Are you sure you want to duplicate this experiment?',
  },
  archive: {
    title: 'Archive Experiment',
    text: 'Are you sure you want to archive this experiment?',
  },
  delete: {
    title: 'Delete Experiment',
    text: 'Are you sure you want to delete this experiment?',
  },
};
